// filepath: frontend-vue/scenario-doctor/src/stores/auth.ts
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import axios from 'axios';
import { useRouter } from 'vue-router';
import {
  isGlobalAdmin,
  canModifyEvents,
  canModifyScenario,
  canDeleteScenario,
  canManageUsers,
  canViewScenario,
  canReorderTimeline,
  isCoordinator,
  type PermissionContext
} from '@/helpers/permissions';
import type { Scenario } from '@/types/scenario';
import type { UserRole, SectionPermissions } from '@/types/user';

// Set axios base URL from env (fallback to default if not set)
axios.defaults.baseURL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8001/api';

// Try to restore user from localStorage
const storedUser = localStorage.getItem('user');
const initialUser = storedUser ? JSON.parse(storedUser) : null;
const storedToken = localStorage.getItem('token');
const storedForceReset = localStorage.getItem('forcePasswordReset');

export const useAuthStore = defineStore('auth', () => {
  const user = ref(initialUser);
  const token = ref(storedToken || '');
  const forcePasswordReset = ref(storedForceReset === 'true');
  const isAuthenticated = computed(() => !!token.value && !!user.value);
  const hasInitializedAuth = ref(false);

  const setupAxiosInterceptor = () => {
    if (token.value) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token.value}`;
    }

    // Add response interceptor to handle 401 errors and password reset requirement
    axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Auto-logout on 401 unauthorized
          logout();
        } else if (error.response?.status === 403 && error.response?.headers?.['x-password-reset-required']) {
          // Password reset required
          forcePasswordReset.value = true;
          localStorage.setItem('forcePasswordReset', 'true');
        }
        return Promise.reject(error);
      }
    );
  };

  // Initialize axios interceptor on store creation
  setupAxiosInterceptor();

  const login = async (username: string, password: string) => {
    try {
      const response = await axios.post('/auth/login', { username, password });
      token.value = response.data.access_token;
      user.value = response.data.user;
      forcePasswordReset.value = response.data.force_password_reset || false;
      localStorage.setItem('token', token.value);
      localStorage.setItem('user', JSON.stringify(user.value));
      localStorage.setItem('forcePasswordReset', String(forcePasswordReset.value));
      axios.defaults.headers.common['Authorization'] = `Bearer ${token.value}`;
      // Automatically check for expired token
      setupTokenExpirationCheck();

      return { success: true, message: 'Login successful', forcePasswordReset: forcePasswordReset.value };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const message = error.response?.data?.detail || error.response?.data?.message || 'Login failed';
        return { success: false, message };
      }
      return { success: false, message: 'Login failed' };
    }
  };

  const refreshCurrentUser = async () => {
    if (!token.value) {
      hasInitializedAuth.value = true;
      return { success: false, message: 'No token' };
    }

    try {
      const response = await axios.get('/auth/me');
      user.value = response.data;
      localStorage.setItem('user', JSON.stringify(user.value));
      hasInitializedAuth.value = true;
      return { success: true };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 401 || error.response?.status === 403) {
          logout();
        }
      }
      hasInitializedAuth.value = true;
      return { success: false, message: 'Failed to refresh current user' };
    }
  };

  const initializeAuth = async () => {
    if (hasInitializedAuth.value) {
      return;
    }

    await refreshCurrentUser();
  };

  const setupTokenExpirationCheck = () => {
    // Check token expiration every 5 minutes
    setInterval(() => {
      if (isTokenExpired()) {
        logout();
      }
    }, 5 * 60 * 1000);
  };

  const isTokenExpired = () => {
    if (!token.value) return true;
    try {
      const payload = JSON.parse(atob(token.value.split('.')[1]));
      return Date.now() >= payload.exp * 1000;
    } catch {
      return true;
    }
  };

  const hasRole = (role: string) => {
    return user.value?.role === role;
  };

  const hasAnyRole = (roles: string[]) => {
    return user.value?.role && roles.includes(user.value.role);
  };

  const getEffectiveSectionPermissions = (raw?: SectionPermissions | null) => {
    return {
      msel_doctor: raw?.msel_doctor ?? true,
      msel_doctor_edit: raw?.msel_doctor_edit ?? false,
      metl_smith: raw?.metl_smith ?? false,
      metl_smith_edit: raw?.metl_smith_edit ?? false
    }
  }

  const hasModuleAccess = (moduleName: string) => {
    if (!user.value) return false
    if (user.value.role === 'admin') return true

    const perms = getEffectiveSectionPermissions(user.value.section_permissions)
    if (moduleName === 'msel-doctor') return perms.msel_doctor
    if (moduleName === 'metl-smith') return perms.metl_smith
    return true
  }

  const canEditModule = (moduleName: string) => {
    if (!user.value) return false
    if (user.value.role === 'admin') return true

    const perms = getEffectiveSectionPermissions(user.value.section_permissions)
    if (moduleName === 'msel-doctor') return perms.msel_doctor_edit
    if (moduleName === 'metl-smith') return perms.metl_smith_edit
    return false
  }

  /**
   * Check if current user is a global admin
   */
  const isAdmin = computed(() => {
    return user.value?.role ? isGlobalAdmin(user.value.role as UserRole) : false;
  });

  /**
   * Check if current user can modify events in a scenario
   * @param scenario - The scenario to check
   * @param logDenial - Whether to log permission denial to console
   */
  const canUserModifyEvents = (scenario: Scenario | null | undefined, logDenial = false) => {
    if (!user.value?.role) return false;
    const context: PermissionContext = {
      userGlobalRole: user.value.role as UserRole,
      userId: user.value.id,
      scenario
    };
    return canModifyEvents(context, logDenial);
  };

  /**
   * Check if current user can modify scenario settings
   * @param scenario - The scenario to check
   * @param logDenial - Whether to log permission denial to console
   */
  const canUserModifyScenario = (scenario: Scenario | null | undefined, logDenial = false) => {
    if (!user.value?.role) return false;
    const context: PermissionContext = {
      userGlobalRole: user.value.role as UserRole,
      userId: user.value.id,
      scenario
    };
    return canModifyScenario(context, logDenial);
  };

  /**
   * Check if current user can delete scenarios
   * @param logDenial - Whether to log permission denial to console
   */
  const canUserDeleteScenario = (logDenial = false) => {
    return user.value?.role ? canDeleteScenario(user.value.role as UserRole, logDenial) : false;
  };

  /**
   * Check if current user can manage users
   * @param logDenial - Whether to log permission denial to console
   */
  const canUserManageUsers = (logDenial = false) => {
    return user.value?.role ? canManageUsers(user.value.role as UserRole, logDenial) : false;
  };

  /**
   * Check if current user can view a scenario
   * @param scenario - The scenario to check
   * @param logDenial - Whether to log permission denial to console
   */
  const canUserViewScenario = (scenario: Scenario | null | undefined, logDenial = false) => {
    if (!user.value?.role) return false;
    const context: PermissionContext = {
      userGlobalRole: user.value.role as UserRole,
      userId: user.value.id,
      scenario
    };
    return canViewScenario(context, logDenial);
  };

  /**
   * Check if current user can reorder timeline elements (drag/drop threads and events)
   * @param scenario - The scenario to check
   * @param logDenial - Whether to log permission denial to console
   */
  const canUserReorderTimeline = (scenario: Scenario | null | undefined, logDenial = false) => {
    if (!user.value?.role) return false;
    const context: PermissionContext = {
      userGlobalRole: user.value.role as UserRole,
      userId: user.value.id,
      scenario
    };
    return canReorderTimeline(context, logDenial);
  };

  /**
   * Check if current user is a coordinator in the scenario
   * @param scenario - The scenario to check
   */
  const isUserCoordinator = (scenario: Scenario | null | undefined) => {
    if (!user.value?.role) return false;
    const context: PermissionContext = {
      userGlobalRole: user.value.role as UserRole,
      userId: user.value.id,
      scenario
    };
    return isCoordinator(context);
  };

  const logout = () => {
    token.value = '';
    user.value = null;
    forcePasswordReset.value = false;
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('forcePasswordReset');
    delete axios.defaults.headers.common['Authorization'];

    // Clear all other stores to prevent data bleed across logins
    // Use dynamic imports to avoid circular dependencies
    import('@/modules/msel-doctor/stores/scenario').then(({ useScenarioStore }) => {
      const scenarioStore = useScenarioStore();
      scenarioStore.clearScenarioData();
      scenarioStore.scenarios = [];
      scenarioStore.selectedScenarioId = null;
    });

    import('./user').then(({ useUserStore }) => {
      const userStore = useUserStore();
      userStore.clearCache();
    });

    import('./users').then(({ useUsersStore }) => {
      const usersStore = useUsersStore();
      usersStore.users = [];
      usersStore.selectedUser = null;
      usersStore.workingGroups = [];
    });

    import('@/modules/metl-smith/stores/tasks').then(({ useTasksStore }) => {
      const tasksStore = useTasksStore();
      tasksStore.standards = [];
      tasksStore.taskHierarchy = [];
      tasksStore.availableMoes = [];
      tasksStore.availableMops = [];
      tasksStore.selectedStandardId = null;
    });
  };

  const generatePassword = async () => {
    try {
      const response = await axios.get('/auth/generate-password');
      return { success: true, password: response.data.password };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const message = error.response?.data?.detail || 'Failed to generate password';
        return { success: false, message };
      }
      return { success: false, message: 'Failed to generate password' };
    }
  };

  const resetPassword = async (oldPassword: string | null, newPassword: string) => {
    try {
      const payload: { old_password?: string; new_password: string } = {
        new_password: newPassword
      };
      if (oldPassword) {
        payload.old_password = oldPassword;
      }
      await axios.post('/auth/reset-password', payload);
      forcePasswordReset.value = false;
      localStorage.removeItem('forcePasswordReset');
      return { success: true, message: 'Password reset successful' };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const message = error.response?.data?.detail || 'Password reset failed';
        return { success: false, message };
      }
      return { success: false, message: 'Password reset failed' };
    }
  };

  return {
    user,
    token,
    forcePasswordReset,
    isAuthenticated,
    isAdmin,
    login,
    initializeAuth,
    refreshCurrentUser,
    logout,
    generatePassword,
    resetPassword,
    isTokenExpired,
    hasRole,
    hasAnyRole,
    hasModuleAccess,
    canEditModule,
    getEffectiveSectionPermissions,
    canUserModifyEvents,
    canUserModifyScenario,
    canUserDeleteScenario,
    canUserManageUsers,
    canUserViewScenario,
    canUserReorderTimeline,
    isUserCoordinator
  };
});
